import React from "react";
import {
  Box,
  Stack,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import NewBadge from "../../common/NewBadge";
import Pill from "../../common/Pill";
import { SalonReport } from "../../../types/reports";
import { ReportCatalog, reportDisplayName } from "./reportCatalog";
import { reportIcon } from "./reportIcons";
import { useReportSections } from "./useReportSections";
import styles from "./ReportsSidebar.module.css";

export { SIDEBAR_SEARCH_THRESHOLD } from "./useReportSections";

export interface ReportsSidebarProps {
  reports: SalonReport[];
  catalog: ReportCatalog;
  /** NEW reports in the Add-a-report picker ("N new" pill; newEntryCount). */
  newCount: number;
  activeReportId: string | null;
  /** Reports with unsaved view changes (amber dot). */
  dirtyReportIds?: ReadonlySet<string>;
  onSelect: (report: SalonReport) => void;
  onAddReport: () => void;
  /** Disable "Add report" (e.g. while the catalog is still loading). */
  addDisabled?: boolean;
}

/**
 * The user's personal report list, under the page title ("Reports &
 * Summaries" lives here rather than in a header bar, leaving the report the
 * full height): a dashed "Add report" button (with an
 * "N new" pill matching the picker's What's new count), a search field for long lists,
 * and one section per catalog category (Legacy last). Rows show the report's
 * icon, an ellipsized name, a NEW badge for new report types, and an unsaved
 * dot.
 */
export default function ReportsSidebar({
  reports,
  catalog,
  newCount,
  activeReportId,
  dirtyReportIds,
  onSelect,
  onAddReport,
  addDisabled = false,
}: ReportsSidebarProps) {
  const { query, setQuery, showSearch, sections } = useReportSections(
    reports,
    catalog,
  );

  return (
    <Box component="nav" aria-label="Reports" className={styles.sidebar}>
      <Box className={styles.top}>
        <Title order={1} className={styles.title}>
          Reports &amp; Summaries
        </Title>
        <UnstyledButton
          type="button"
          className={styles.addButton}
          onClick={onAddReport}
          disabled={addDisabled}
          aria-label={
            newCount > 0 ? `Add report (${newCount} new)` : "Add report"
          }
        >
          <IconPlus size={15} aria-hidden />
          Add report
          {newCount > 0 && <Pill aria-hidden>{newCount} new</Pill>}
        </UnstyledButton>
        {showSearch && (
          <TextInput
            size="xs"
            classNames={{ input: styles.searchInput }}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            placeholder="Search your reports"
            aria-label="Search your reports"
            leftSection={<IconSearch size={13} aria-hidden />}
          />
        )}
      </Box>

      <Box className={styles.list}>
        {sections.map(({ category, reports: sectionReports }) => (
          <Box key={category.key}>
            <Text className={styles.sectionLabel}>{category.label}</Text>
            <Stack gap={2}>
              {sectionReports.map((report) => {
                const entry = catalog.byType.get(report.report_type);
                const Icon = reportIcon(entry?.icon);
                const active = report.id === activeReportId;
                const name = reportDisplayName(report, catalog);
                const dirty = dirtyReportIds?.has(report.id) ?? false;
                return (
                  <UnstyledButton
                    key={report.id}
                    type="button"
                    className={styles.item}
                    aria-current={active ? "page" : undefined}
                    title={name}
                    onClick={() => {
                      if (!active) onSelect(report);
                    }}
                  >
                    <Box component="span" className={styles.itemIcon}>
                      <Icon size={16} aria-hidden />
                    </Box>
                    <Text component="span" inherit className={styles.itemName}>
                      {name}
                    </Text>
                    {entry?.engine === "v2" && entry.isNew && (
                      <NewBadge inverted={active} />
                    )}
                    {dirty && (
                      <Box
                        component="span"
                        className={styles.dirtyDot}
                        role="img"
                        aria-label="Unsaved changes"
                        title="Unsaved changes"
                      />
                    )}
                  </UnstyledButton>
                );
              })}
            </Stack>
          </Box>
        ))}
        {sections.length === 0 && reports.length > 0 && (
          <Text className={styles.noMatch}>No reports match</Text>
        )}
      </Box>
    </Box>
  );
}
